import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalModal } from '../contexts/ModalContext';
import { useMilestones, milestoneAnalytics } from '../contexts/MilestoneContext';
import { useResourceCelebration } from '../contexts/ResourceCelebrationContext';
import { useGameStartAccountOffer } from '../contexts/GameStartAccountOfferContext';
import { useAds } from '../contexts/AdsContext';
import { useAIHelper } from '../contexts/AIHelperContext';
import { useUserProfile } from '../contexts/UserProfileContext';
import { logEvent } from '../analytics';
import MilestoneCelebration from './MilestoneCelebration';
import styles from '../styles/MilestoneCelebration.module.css';
import { RESOURCE_RECEIVE_DURATION_MS } from '../utils/resourceCelebrationTiming';
import { celebrationEventName, celebrationTheme } from '../utils/milestones';
import { getCurrentDate } from '../util';

const MilestoneCoordinator = ({ isOtherModalOpen = false }) => {
  const { pending, finishMilestone } = useMilestones();
  const { openModal, closeModal, isOpen, modalName } = useGlobalModal();
  const { flights = [] } = useResourceCelebration();
  const { hasPendingOffer, gamePresentationPending, isGameplayActive } = useGameStartAccountOffer();
  const { isAdBreakActive } = useAds() || {};
  const { isOpen: isAIHelperOpen } = useAIHelper();
  const { isLoading: isProfileLoading } = useUserProfile();
  const location = useLocation();
  const activeRef = useRef(null);
  const item = pending.find((entry) => entry.event.kind !== 'daily_circuit') || pending[0];
  useEffect(() => {
    if (activeRef.current && !pending.some((entry) => entry.event.id === activeRef.current)) {
      activeRef.current = null;
      if (isOpen && ['milestone_celebration', 'daily_circuit_celebration'].includes(modalName)) closeModal('account_changed');
    }
  }, [pending, isOpen, modalName, closeModal]);
  useEffect(() => {
    if (!item || activeRef.current || isOpen || isOtherModalOpen || flights.length || hasPendingOffer
      || gamePresentationPending || isGameplayActive || isAdBreakActive || isAIHelperOpen || isProfileLoading
      || /^\/(admin|preview|signup|signin)(\/|$)/.test(location.pathname)) return undefined;
    const timer = setTimeout(() => {
      if (activeRef.current) return;
      const isCircuit = item.event.kind === 'daily_circuit';
      if (isCircuit && item.event.date !== getCurrentDate()) {
        finishMilestone(item.event.id);
        return;
      }
      activeRef.current = item.event.id;
      openModal({ title: 'Traguardo raggiunto!', showHeader: false, trapFocus: true,
        containerClassName: `${styles.modalContainer} ${styles[celebrationTheme(item.event)]}`,
        celebrationType: 'confetti', content: <MilestoneCelebration key={item.event.id} event={item.event} />,
        onClose: (reason) => {
          activeRef.current = null;
          finishMilestone(item.event.id);
          logEvent(celebrationEventName(item.event, 'dismissed'), { ...milestoneAnalytics(item.event), reason });
        },
        analytics: { source_surface: isCircuit ? 'daily_circuit' : 'milestone',
          modal_name: isCircuit ? 'daily_circuit_celebration' : 'milestone_celebration',
          trigger: isCircuit ? 'daily_circuit_completion' : 'resource_milestone' },
      });
      logEvent(celebrationEventName(item.event, 'displayed'), milestoneAnalytics(item.event));
    // Reduced-motion flights leave their queue before the 700ms counter pulse
    // finishes. Give the final impact a full receive interval in either mode.
    }, Math.max(RESOURCE_RECEIVE_DURATION_MS, item.readyAt - Date.now()));
    return () => clearTimeout(timer);
  }, [item, isOpen, isOtherModalOpen, flights.length, hasPendingOffer, gamePresentationPending, isGameplayActive,
    isAdBreakActive, isAIHelperOpen, isProfileLoading, location.pathname, openModal, finishMilestone]);
  return null;
};
export default MilestoneCoordinator;
